import type { WorkoutPreview } from "./workoutPreview";

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE PHONE'S WORKOUT OVERVIEW, PORTED FROM ITS SOURCE — W66, his redirect of 2026-09-24 ~22:40:
 * *"make it same exact like the client sees in the workout overview page … get it from the same
 * source … just without the start workout button and no settings and back button … even the dots
 * with the lines are not exactly as should."*
 *
 * The web cannot run SwiftUI, so "the same source" is `PreWorkoutBriefingView.swift` read line by
 * line: every number below is that file's number (or `BriefingMetrics`', `HT.Spacing`'s,
 * `SessionEquipmentRow`'s), and `briefing.test.ts` reads the Swift ?raw and finds each one beside
 * its Swift name — so the day somebody retunes the phone, this goes red instead of drifting
 * (Fable's condition 1; the codex's two-numbers trap).
 *
 * WHAT IS DELIBERATELY NOT DRAWN (his words): the back and settings discs, the Start workout pill.
 * And by the phone's own rule, not a choice: the (i) — `vm.isEngineAuthored` only, and every
 * workout in a coach's assign window is the coach's.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** `HT.Spacing` (HTLayout.swift). */
export const SPACING = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 24 } as const;

/** `BriefingMetrics` and the literals of `BriefingExerciseList` / `SpineOverlay` / the row. */
export const BRIEFING = {
  rowLeading: SPACING.md,        // BriefingMetrics.rowLeading = HT.Spacing.md
  connectorWidth: 38,            // BriefingMetrics.connectorWidth
  thumbSize: 56,                 // BriefingMetrics.thumbSize
  thumbGap: SPACING.sm,          // BriefingMetrics.thumbGap = HT.Spacing.sm
  rowMinHeight: 68,              // .frame(minHeight: 68)
  dotR: 4.5,                     // SpineOverlay: let dotR: CGFloat = 4.5
  corner: 10,                    // SpineOverlay: let corner: CGFloat = 10
  dash: 4,                       // StrokeStyle(lineWidth: 1.5, dash: [4, 4])
  spineWidth: 1.5,
  arrowBack: 6,                  // arrowhead: tip.x ± 6
  arrowHalf: 4,                  // arrowhead: tip.y ± 4
  cardRadius: 14,                // HT.Sweep.card → HT.Radius.large
  headerMinHeight: 44,           // HT.Layout.minTapTarget
  titleSize: 34,                 // Text(sessionName).htScaled(34, weight: .semibold)
  thumbRadius: 4,                // ExerciseVideoThumbnail cornerRadius = HT.Radius.small
  monogramScale: 0.42,           // ExerciseMonogram: side * 0.42
  equipmentIcon: 20,             // SessionEquipmentRow: .frame(width: 20, height: 20)
} as const;

/** Derived exactly as the phone derives them. */
export const SPINE_X = (BRIEFING.rowLeading + BRIEFING.connectorWidth) / 2;                   // 27
export const CARD_LEADING = BRIEFING.rowLeading + BRIEFING.connectorWidth - 5;                  // 49
export const DIVIDER_LEADING = SPACING.sm + BRIEFING.thumbSize + BRIEFING.thumbGap;             // 80

export type Connector = "standalone" | "supersetFirst" | "supersetMiddle" | "supersetLast";

export type BriefingRow = {
  id: string;
  name: string;
  dose: string;
  exerciseId: string | null;
  connector: Connector;
  /** The fading hairline under this row — never inside a superset (`suppressesDividerAfter`). */
  dividerAfter: boolean;
  /** W137 — carried from the preview row for the Week's extended column only (the phone's row prints neither). */
  note: string | null;
  rest: string | null;
};

export type FormatFigure = { symbol: "clock" | "cycle"; text: string };

export type BriefingBlock = {
  key: string;
  /** `BriefingSectionHeader`, or null for `BriefingMainBand` — which prints `MAIN`. */
  header: { title: string; figure: FormatFigure | null; hasNote: boolean } | null;
  rows: BriefingRow[];
};

/**
 * `AssignedWorkoutBridge`'s section name: the coach's, else `Superset` for a superset-format
 * section, else `MAIN` — and an IMPLICIT section has none. 🔴 This is NOT `sectionDisplayName`'s
 * `Section N`: the phone says `MAIN` where the builder and the calendar say `Section 3`. The
 * preview follows the PHONE because it shows what the client sees (Fable's condition 2); the
 * divergence itself is filed for a product decision, never silently unified here.
 */
export function phoneSectionName(rawName: string | null, format: string, implicit: boolean): string {
  if (implicit) return "";
  return rawName ?? (format === "superset" ? "Superset" : "MAIN");
}

/** `SectionHeaderInfo.hasRealName`: implicit first, then the two role words that are not names. */
export function hasRealName(name: string, implicit: boolean): boolean {
  if (implicit) return false;
  return !["Superset", "Main Workout"].includes(name);
}

/**
 * `WorkoutSection.formatFigure`: AMRAP/EMOM a clock + the config's headline, circuit/for-time the
 * cycle arrow + the headline, straight sets and superset nothing. ⚠️ TIMED INTERVAL is the phone's
 * total of its segments (`SectionSchedule.totalSeconds`), which the web does not port yet — it
 * draws NO figure rather than a different number, and says so here.
 */
export function formatFigure(format: string, headline: string | null): FormatFigure | null {
  if (headline === null) return null;
  switch (format) {
    case "amrap": case "emom": return { symbol: "clock", text: headline.toUpperCase() };
    case "circuit": case "for_time": return { symbol: "cycle", text: headline.toUpperCase() };
    default: return null;
  }
}

/** Supersets as the phone groups them: a run of `superset_with_next` rows inside ONE section. */
function connectorsFor(flags: readonly boolean[]): Connector[] {
  const out: Connector[] = [];
  let i = 0;
  while (i < flags.length) {
    let j = i;
    while (j < flags.length - 1 && flags[j] === true) j += 1;
    const n = j - i + 1;
    for (let k = 0; k < n; k++) {
      out.push(n === 1 ? "standalone" : k === 0 ? "supersetFirst" : k === n - 1 ? "supersetLast" : "supersetMiddle");
    }
    i = j + 1;
  }
  return out;
}

/**
 * `BriefingExerciseList.blocks`, ported: a section with a real name opens a card with its own
 * header; every unnamed item (implicit, or a `Superset`-named section) joins the header-less card
 * before it, or opens one — drawn with the `MAIN` band.
 */
export function briefingBlocks(preview: WorkoutPreview): BriefingBlock[] {
  const out: BriefingBlock[] = [];
  let lastSection = -1;
  preview.sections.forEach((s, si) => {
    const name = phoneSectionName(s.rawName, s.format, s.implicit);
    const real = hasRealName(name, s.implicit);
    const connectors = connectorsFor(s.rows.map((r) => r.supersetWithNext));
    s.rows.forEach((r, ri) => {
      const connector = connectors[ri] ?? "standalone";
      const row: BriefingRow = {
        id: r.exerciseRowId, name: r.name, dose: r.phoneDose, exerciseId: r.exerciseId, connector,
        dividerAfter: true, note: r.note, rest: r.rest,
      };
      const last = out[out.length - 1];
      if (real && ri === 0) {
        out.push({ key: `s${String(si)}`, rows: [row], header: {
          title: name.toUpperCase(), figure: formatFigure(s.format, s.formatLine), hasNote: s.description !== null,
        } });
      } else if (real && last !== undefined && last.header !== null && lastSection === si) {
        last.rows.push(row);
      } else if (last !== undefined && last.header === null) {
        last.rows.push(row);
      } else {
        out.push({ key: `loose-${r.exerciseRowId}`, header: null, rows: [row] });
      }
      lastSection = si;
    });
  });
  // The hairline sits BETWEEN rows of a card and never inside a superset.
  for (const b of out) {
    b.rows.forEach((row, i) => {
      row.dividerAfter = i < b.rows.length - 1 && row.connector !== "supersetFirst" && row.connector !== "supersetMiddle";
    });
  }
  return out;
}

/** `ExerciseVideoURL.youtubeId(from:)`, ported: a youtu.be path, an /embed/ path, or `?v=`. */
export function phoneYoutubeId(raw: string | null): string | null {
  const s = (raw ?? "").trim();
  if (s === "") return null;
  let u: URL;
  try { u = new URL(s); } catch { return null; }
  const host = u.hostname.toLowerCase();
  if (!host.includes("youtube") && !host.includes("youtu.be")) return null;
  const last = u.pathname.split("/").filter(Boolean).pop() ?? "";
  if (host.includes("youtu.be")) return last === "" ? null : last;
  if (u.pathname.startsWith("/embed/")) return last === "" ? null : last;
  const v = u.searchParams.get("v");
  return v === null || v === "" ? null : v;
}

/** `ExerciseVideoURL.resolvedThumbnailURL`: the stored thumbnail, else YouTube's hqdefault, else none. */
export function phoneThumbnail(demoVideoUrl: string | null, thumbnailUrl: string | null): string | null {
  const stored = (thumbnailUrl ?? "").trim();
  if (stored !== "") return stored;
  const id = phoneYoutubeId(demoVideoUrl);
  return id === null ? null : `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

/** `ExerciseMonogram.initial`: the first character if it is a LETTER, uppercased; else null (a figure glyph). */
export function monogramInitial(name: string): string | null {
  const c = name.charAt(0);
  return /\p{L}/u.test(c) ? c.toUpperCase() : null;
}
